/**
 * TrialKeepToolModal — the first-use prompt (TRIAL_PLAN.md §8.5). Fires the
 * first time a trial creator opens a tool that would lock on day 14, once per
 * SKU. It never blocks: the tool is already open behind it, and "Just
 * exploring" costs nothing.
 *
 * Driven by the queue in lib/trial.js — gated surfaces call
 * useTrialKeepPrompt(sku) on mount.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Crown } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  useTrial, useKeepPromptQueue, dequeueKeepPrompt, ackTrial, setTrial,
  getProCatalog, flattenCatalog, money, trialDate,
} from "@/lib/trial";

export default function TrialKeepToolModal() {
  const { trial } = useTrial();
  const queue = useKeepPromptQueue();
  const sku = queue[0] || null;
  const [node, setNode] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!sku) { setNode(null); return; }
    let alive = true;
    getProCatalog().then((cat) => {
      if (!alive) return;
      setNode(flattenCatalog(cat).find((l) => l.key === sku) || null);
    });
    return () => { alive = false; };
  }, [sku]);

  if (!sku || !node || !trial?.live) return null;

  const close = async (acked = true) => {
    if (acked) await ackTrial("prompted", sku);
    dequeueKeepPrompt(sku);
  };

  const keep = async () => {
    setBusy(true);
    try {
      const r = await api.post("/trial/commit", {
        sku, price_cents: node.day1_cents, screen: "first_use",
      });
      setTrial(r.data?.trial);
      toast.success(`${node.name} is on your day-14 bill — ${money(node.day1_cents)} on ${trialDate(trial.ends_at)}. Remove it any time before then.`);
      await close(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add that to your list.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) close(true); }}>
      <DialogContent
        className="max-w-md bg-gradient-to-br from-[#1C1B1A] to-[#3A4A3E] text-[#FDFCF8] border-0"
        data-testid="trial-keep-modal"
      >
        <DialogTitle className="sr-only">Keep {node.name} after the trial?</DialogTitle>
        <DialogDescription className="sr-only">
          Nothing is charged today. {node.name} costs {money(node.day1_cents)} on day 14 if you keep it.
        </DialogDescription>

        <div className="text-[10px] uppercase tracking-[0.3em] text-[#C16E5A] flex items-center gap-2">
          <Crown size={12} weight="duotone" /> Part of your trial
        </div>
        <h3 className="font-serif-display text-2xl leading-tight mt-1">
          {node.name} is <em className="text-[#C16E5A]">part of your trial</em>.
        </h3>
        <p className="text-sm text-[#E5E5E0]/90 mt-3">
          Keep it after {trialDate(trial.ends_at)} for a one-time{" "}
          <strong className="text-white">{money(node.day1_cents)}</strong> — or leave it free for now
          and it will lock when the trial ends. Either way, nothing is charged today.
        </p>

        <div className="mt-5 flex flex-col sm:flex-row gap-2.5">
          <button
            onClick={keep}
            disabled={busy}
            data-testid="trial-keep-yes"
            className="flex-1 bg-[#C16E5A] text-white hover:bg-[#A85E4A] px-5 py-3 rounded-md text-sm font-medium transition-colors disabled:opacity-60"
          >
            {busy ? "Adding…" : "Keep it after the trial"}
          </button>
          <button
            onClick={() => close(true)}
            disabled={busy}
            data-testid="trial-keep-no"
            className="sm:w-auto border border-white/25 text-[#E5E5E0] hover:bg-white/10 px-5 py-3 rounded-md text-sm transition-colors disabled:opacity-60"
          >
            Just exploring
          </button>
        </div>
        <p className="text-[11px] text-[#A8A8A0] mt-3">
          You can change this any time before your trial ends, from Account &amp; Billing.
        </p>
      </DialogContent>
    </Dialog>
  );
}
